import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isEmail } from "validator";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Alert,
  Collapse,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { keyframes } from "@mui/system";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import AuthService from "../services/auth.service";
import { tokens } from "../theme";

// Brand palette lifted from the Storemax logo (navy → steel blue, silver "MAX").
const BRAND = {
  navyDeep: "#0A2038",
  navy: "#12395F",
  blue: "#1E5B8F",
  blueBright: "#2E7DB8",
  steel: "#5B9BD5",
  silver: "#9AA7B4",
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Same validation rules as before (3–20 chars, valid email, 6–40 char password),
// just surfaced inline instead of via react-validation.
const rules = {
  firstName: (v) =>
    !v.trim()
      ? "First name is required"
      : v.trim().length < 3 || v.trim().length > 20
      ? "Must be 3–20 characters"
      : "",
  lastName: (v) =>
    !v.trim()
      ? "Last name is required"
      : v.trim().length < 3 || v.trim().length > 20
      ? "Must be 3–20 characters"
      : "",
  username: (v) =>
    !v.trim()
      ? "Username is required"
      : v.trim().length < 3 || v.trim().length > 20
      ? "Must be 3–20 characters"
      : "",
  email: (v) =>
    !v.trim() ? "Email is required" : !isEmail(v) ? "Enter a valid email" : "",
  password: (v) =>
    !v
      ? "Password is required"
      : v.length < 6 || v.length > 40
      ? "Must be 6–40 characters"
      : "",
};

const Register = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [successful, setSuccessful] = useState(false);
  const [message, setMessage] = useState("");

  const values = { firstName, lastName, username, email, password };
  const errorFor = (field) => (touched[field] ? rules[field](values[field]) : "");
  const setField = (field, setter) => (e) => {
    setter(e.target.value);
  };
  const blur = (field) => () => setTouched((t) => ({ ...t, [field]: true }));

  // Preserve the original flow: on success, log the just-created user out and
  // send them to the login screen to sign in.
  useEffect(() => {
    if (successful) {
      AuthService.logout()
        .then(() => navigate("/login"))
        .catch((err) => console.log(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successful]);

  const handleRegister = (e) => {
    e.preventDefault();
    setMessage("");
    setSuccessful(false);
    setTouched({
      firstName: true,
      lastName: true,
      username: true,
      email: true,
      password: true,
    });

    const hasError = Object.keys(rules).some((f) => rules[f](values[f]));
    if (hasError) return;

    setLoading(true);
    AuthService.register(firstName, lastName, username, email, password)
      .then((response) => {
        setMessage(response.id);
        setSuccessful(true);
      })
      .catch((error) => {
        const resMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setLoading(false);
        setMessage(resMessage);
        setSuccessful(false);
      });
  };

  const inputBg = isDark ? "#16233B" : "#ffffff";
  const inputText = colors.grey[100];

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: inputBg,
      transition: "border-color .15s ease, box-shadow .15s ease",
      "& fieldset": {
        borderColor: isDark ? colors.grey[700] : colors.grey[300],
      },
      "&:hover fieldset": { borderColor: BRAND.steel },
      "&.Mui-focused fieldset": {
        borderColor: BRAND.blueBright,
        borderWidth: "2px",
      },
      "&.Mui-focused": { boxShadow: `0 0 0 4px ${BRAND.blueBright}22` },
    },
    "& .MuiOutlinedInput-input": {
      color: inputText,
      WebkitTextFillColor: inputText,
      "&::placeholder": { color: colors.grey[500], opacity: 1 },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: BRAND.blueBright },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active":
      {
        WebkitBoxShadow: `0 0 0 100px ${inputBg} inset !important`,
        WebkitTextFillColor: `${inputText} !important`,
        caretColor: inputText,
        borderRadius: "inherit",
        transition: "background-color 9999s ease-in-out 0s",
      },
  };

  const iconSx = { color: colors.grey[400] };

  return (
    <Box
      display="flex"
      minHeight="100vh"
      width="100%"
      sx={{ backgroundColor: isDark ? colors.primary[500] : "#f4f6f9" }}
    >
      {/* Left brand panel */}
      {!isSmall && (
        <Box
          flex="1"
          position="relative"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          p={7}
          sx={{
            background: `linear-gradient(150deg, ${BRAND.navyDeep} 0%, ${BRAND.navy} 55%, ${BRAND.blue} 115%)`,
            color: "#fff",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `linear-gradient(${BRAND.steel}22 1px, transparent 1px),
                linear-gradient(90deg, ${BRAND.steel}22 1px, transparent 1px)`,
              backgroundSize: "46px 46px",
              maskImage:
                "radial-gradient(circle at 30% 40%, black, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(circle at 30% 40%, black, transparent 75%)",
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: "-140px",
              right: "-120px",
              width: "420px",
              height: "420px",
              borderRadius: "50%",
              background: `radial-gradient(circle at 30% 30%, ${BRAND.steel}55, transparent 70%)`,
              filter: "blur(14px)",
            }}
          />

          <Typography variant="h4" fontWeight={800} letterSpacing="4px" zIndex={1}>
            <span style={{ color: "#fff" }}>STORE</span>
            <span style={{ color: BRAND.silver }}>MAX</span>
          </Typography>

          <Box zIndex={1} maxWidth="440px">
            <Typography variant="h2" fontWeight={800} lineHeight={1.15} mb={2}>
              Get started.
            </Typography>
            <Typography variant="h6" fontWeight={400} sx={{ opacity: 0.82 }}>
              Create your account to start building quotations and managing your
              inventory, it only takes a minute.
            </Typography>
          </Box>

          <Box zIndex={1}>
            <Typography
              variant="body2"
              sx={{
                letterSpacing: "3px",
                textTransform: "uppercase",
                color: BRAND.steel,
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              Material Handling Experts
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.5 }}>
              © {new Date().getFullYear()} Storemax. All rights reserved.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Right form panel */}
      <Box
        flex="1"
        position="relative"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={3}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "-10%",
            right: "-6%",
            width: 460,
            height: 460,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.blue}${isDark ? "40" : "18"}, transparent 70%)`,
            filter: "blur(50px)",
            pointerEvents: "none",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: "-14%",
            left: "-8%",
            width: 420,
            height: 420,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.steel}${isDark ? "33" : "14"}, transparent 70%)`,
            filter: "blur(55px)",
            pointerEvents: "none",
          }}
        />

        <Box
          component="form"
          onSubmit={handleRegister}
          noValidate
          width="100%"
          maxWidth="460px"
          sx={{
            position: "relative",
            zIndex: 1,
            animation: `${fadeUp} .5s cubic-bezier(.2,.7,.2,1) both`,
            backgroundColor: isDark ? colors.primary[400] : "#fff",
            backgroundImage: isDark
              ? `linear-gradient(160deg, ${BRAND.steel}1a, transparent 42%)`
              : `linear-gradient(160deg, ${BRAND.blue}0d, transparent 42%)`,
            borderRadius: "22px",
            p: { xs: 3, sm: 5 },
            border: `1px solid ${isDark ? colors.grey[800] : colors.grey[200]}`,
            boxShadow: isDark
              ? "0 24px 70px rgba(0,0,0,0.5)"
              : "0 24px 70px rgba(18,57,95,0.12)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "10%",
              right: "10%",
              height: "3px",
              borderRadius: "0 0 4px 4px",
              background: `linear-gradient(90deg, transparent, ${BRAND.blueBright}, ${BRAND.steel}, transparent)`,
            },
          }}
        >
          {/* Custom Storemax brand mark — isometric storage cube (SVG) + wordmark */}
          <Box display="flex" alignItems="center" gap={1.25} mb={3}>
            <Box
              component="svg"
              viewBox="0 0 48 48"
              sx={{
                width: 44,
                height: 44,
                display: "block",
                filter: `drop-shadow(0 6px 14px ${BRAND.navy}77)`,
              }}
            >
              <defs>
                <linearGradient id="smxTopR" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#AECEE8" />
                  <stop offset="100%" stopColor="#7FB0D8" />
                </linearGradient>
                <linearGradient id="smxLeftR" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3E80B5" />
                  <stop offset="100%" stopColor="#2A6096" />
                </linearGradient>
                <linearGradient id="smxRightR" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#21507D" />
                  <stop offset="100%" stopColor="#143A5C" />
                </linearGradient>
              </defs>
              <polygon points="24,11.5 37,18 24,24.5 11,18" fill="url(#smxTopR)" />
              <polygon points="11,18 24,24.5 24,39.5 11,33" fill="url(#smxLeftR)" />
              <polygon points="37,18 24,24.5 24,39.5 37,33" fill="url(#smxRightR)" />
            </Box>
            <Typography variant="h4" fontWeight={800} letterSpacing="2px" lineHeight={1}>
              <span style={{ color: isDark ? "#fff" : BRAND.navy }}>STORE</span>
              <span style={{ color: BRAND.steel }}>MAX</span>
            </Typography>
          </Box>

          <Typography variant="h3" fontWeight={800} mb={0.5} color={colors.grey[100]}>
            Create account
          </Typography>
          <Typography variant="body2" color={colors.grey[400]} mb={3}>
            Join Storemax to manage quotations, carts, and inventory.
          </Typography>

          <Collapse in={!!message}>
            <Alert
              severity={successful ? "success" : "error"}
              sx={{ mb: 2.5, borderRadius: "12px" }}
            >
              {successful ? "Account created — redirecting to sign in…" : message}
            </Alert>
          </Collapse>

          {/* First / last name row */}
          <Box display="flex" gap={1.5} sx={{ flexDirection: { xs: "column", sm: "row" } }}>
            <TextField
              fullWidth
              label="First name"
              value={firstName}
              onChange={setField("firstName", setFirstName)}
              onBlur={blur("firstName")}
              error={!!errorFor("firstName")}
              helperText={errorFor("firstName") || " "}
              autoComplete="given-name"
              sx={{ ...fieldSx, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineRoundedIcon sx={iconSx} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Last name"
              value={lastName}
              onChange={setField("lastName", setLastName)}
              onBlur={blur("lastName")}
              error={!!errorFor("lastName")}
              helperText={errorFor("lastName") || " "}
              autoComplete="family-name"
              sx={{ ...fieldSx, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineRoundedIcon sx={iconSx} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={setField("username", setUsername)}
            onBlur={blur("username")}
            error={!!errorFor("username")}
            helperText={errorFor("username") || " "}
            autoComplete="username"
            sx={{ ...fieldSx, mb: 0.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircleOutlinedIcon sx={iconSx} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Email"
            value={email}
            onChange={setField("email", setEmail)}
            onBlur={blur("email")}
            error={!!errorFor("email")}
            helperText={errorFor("email") || " "}
            autoComplete="email"
            sx={{ ...fieldSx, mb: 0.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={iconSx} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={setField("password", setPassword)}
            onBlur={blur("password")}
            error={!!errorFor("password")}
            helperText={errorFor("password") || " "}
            autoComplete="new-password"
            sx={{ ...fieldSx, mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={iconSx} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            disabled={loading || successful}
            endIcon={!loading && <ArrowForwardRoundedIcon />}
            sx={{
              background: `linear-gradient(135deg, ${BRAND.blue} 0%, ${BRAND.navy} 100%)`,
              color: "#fff",
              fontSize: "15px",
              fontWeight: 700,
              letterSpacing: "0.3px",
              textTransform: "none",
              borderRadius: "12px",
              py: 1.5,
              boxShadow: `0 10px 24px ${BRAND.navy}55`,
              transition: "transform .15s ease, box-shadow .15s ease",
              "& .MuiButton-endIcon": { transition: "transform .18s ease" },
              "&:hover": {
                background: `linear-gradient(135deg, ${BRAND.blueBright} 0%, ${BRAND.blue} 100%)`,
                boxShadow: `0 12px 30px ${BRAND.navy}66`,
                transform: "translateY(-1px)",
              },
              "&:hover .MuiButton-endIcon": { transform: "translateX(3px)" },
              "&.Mui-disabled": {
                background: colors.grey[700],
                color: colors.grey[400],
                boxShadow: "none",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Create account"
            )}
          </Button>

          <Typography variant="body2" color={colors.grey[400]} textAlign="center" mt={3.5}>
            Already have an account?{" "}
            <Box
              component={Link}
              to="/login"
              sx={{
                color: isDark ? BRAND.steel : BRAND.blue,
                fontWeight: 700,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Sign in
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
